import {isFeatureEnabled} from '@github-ui/feature-flags'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload --
   Reading enabled_features because useRoutePayload is not available for Relay routes. */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'

import {createScribeArtifact, deleteScribeArtifact, listScribeArtifacts, updateScribeArtifact} from './services'
import type {
  CreateScribeArtifactInput,
  DeleteScribeArtifactInput,
  DeleteScribeArtifactResult,
  ListScribeArtifactsOptions,
  ScribeArtifact,
  ScribeArtifactResourceId,
  ScribeArtifactResourceType,
  ScribeArtifactsListPayload,
  UpdateScribeArtifactInput,
} from './types'

export function scribeArtifactsQueryKey(
  resourceType: ScribeArtifactResourceType,
  resourceId: ScribeArtifactResourceId,
  type?: string,
): readonly unknown[] {
  return type
    ? (['scribe-artifacts', resourceType, String(resourceId), type] as const)
    : (['scribe-artifacts', resourceType, String(resourceId)] as const)
}

interface UseScribeArtifactsOptions extends Omit<ListScribeArtifactsOptions, 'signal'> {
  enabled?: boolean
}

export function useScribeArtifactsApiEnabled(): boolean {
  const enabledFeatures = useAppPayload<{enabled_features?: Record<string, boolean>}>()?.enabled_features
  return enabledFeatures?.scribe_artifacts_api ?? isFeatureEnabled('scribe_artifacts_api')
}

export function useScribeArtifacts({
  resourceType,
  resourceId,
  type,
  page,
  perPage,
  enabled = true,
}: UseScribeArtifactsOptions) {
  return useQuery<ScribeArtifactsListPayload>({
    queryKey: [...scribeArtifactsQueryKey(resourceType, resourceId, type), {page, perPage}],
    queryFn: ({signal}) => listScribeArtifacts({resourceType, resourceId, type, page, perPage, signal}),
    enabled,
    placeholderData: (previousData, previousQuery) => {
      const previousType = typeof previousQuery?.queryKey[3] === 'string' ? previousQuery.queryKey[3] : undefined
      return previousQuery?.queryKey[1] === resourceType &&
        previousQuery.queryKey[2] === String(resourceId) &&
        previousType === type
        ? previousData
        : undefined
    },
    refetchOnWindowFocus: false,
    staleTime: 30_000,
  })
}

interface UseCreateScribeArtifactOptions {
  resourceType: ScribeArtifactResourceType
  resourceId: ScribeArtifactResourceId
}

type CreateInput = Pick<CreateScribeArtifactInput, 'name' | 'description' | 'body' | 'type'>

export function useCreateScribeArtifact({resourceType, resourceId}: UseCreateScribeArtifactOptions) {
  const queryClient = useQueryClient()

  return useMutation<ScribeArtifact, Error, CreateInput>({
    mutationFn: input => createScribeArtifact({resourceType, resourceId, ...input}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: scribeArtifactsQueryKey(resourceType, resourceId)})
    },
  })
}

interface UseUpdateScribeArtifactOptions {
  resourceType: ScribeArtifactResourceType
  resourceId: ScribeArtifactResourceId
}

type UpdateInput = Pick<UpdateScribeArtifactInput, 'number' | 'name' | 'body'>

export function useUpdateScribeArtifact({resourceType, resourceId}: UseUpdateScribeArtifactOptions) {
  const queryClient = useQueryClient()

  return useMutation<ScribeArtifact, Error, UpdateInput>({
    mutationFn: input => updateScribeArtifact({resourceType, resourceId, ...input}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: scribeArtifactsQueryKey(resourceType, resourceId)})
    },
  })
}

interface UseDeleteScribeArtifactOptions {
  resourceType: ScribeArtifactResourceType
  resourceId: ScribeArtifactResourceId
}

type DeleteInput = Pick<DeleteScribeArtifactInput, 'number'>

export function useDeleteScribeArtifact({resourceType, resourceId}: UseDeleteScribeArtifactOptions) {
  const queryClient = useQueryClient()

  return useMutation<DeleteScribeArtifactResult, Error, DeleteInput>({
    mutationFn: ({number}) => deleteScribeArtifact({resourceType, resourceId, number}),
    onSuccess: () => {
      void queryClient.invalidateQueries({queryKey: scribeArtifactsQueryKey(resourceType, resourceId)})
    },
  })
}
